import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardDescription, CardContent } from '@/components/ui/card';
import { Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, role, isLoading } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // If already authenticated, redirect to appropriate portal
  useEffect(() => {
    if (!isLoading && isAuthenticated && role) {
      if (role === 'ADMIN') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/user', { replace: true });
      }
    }
  }, [isAuthenticated, role, isLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await login(formData.username, formData.password);
      toast.success(res.message || 'Login successful');

      if (res.role === 'ADMIN') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/user', { replace: true });
      }
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.message || error.response?.data?.detail;

      if (status === 401) {
        toast.error('Invalid username or password');
      } else if (status === 403) {
        toast.error(detail || 'This account has been deactivated. Please contact clinic administrator.');
      } else {
        toast.error(detail || 'Login failed. Please check your credentials and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-4 overflow-y-auto">
      <Card className="w-full max-w-md shadow-xl border-ayurveda-sage-200">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <img src="/logo.png" alt="Sanjivani Ayurvedic Clinic" className="h-24 w-auto object-contain" />
          </div>
          <div>
            <h2 className="text-2xl font-serif font-bold text-ayurveda-herbal-900">Clinic Portal Login</h2>
            <CardDescription className="text-base mt-2 text-ayurveda-sage-600">
              Sign in with your Admin or Staff credentials
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-medium">
                Username
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username"
                  className="pl-10 h-11"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  className="pl-10 h-11"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  autoComplete="current-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 text-base bg-ayurveda-herbal-700 hover:bg-ayurveda-herbal-800 text-white"
              isLoading={submitting}
              disabled={submitting}
            >
              {submitting ? 'Authenticating...' : 'Sign In'}
            </Button>

            <div className="text-center text-xs text-muted-foreground pt-2">
              Secure authentication • Doctor & Staff portal
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
